import { designTokens, RiArrowDownSLine } from "@itandi/itandi-bb-ui";
import { Alert } from "@itandi/itandi-bb-ui/Alert";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Dropdown } from "@itandi/itandi-bb-ui/Dropdown";
import { DropdownMenu } from "@itandi/itandi-bb-ui/DropdownMenu";
import { Label } from "@itandi/itandi-bb-ui/Label";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { Table } from "@itandi/itandi-bb-ui/Table";
import { Tbody } from "@itandi/itandi-bb-ui/Tbody";
import { Td } from "@itandi/itandi-bb-ui/Td";
import { TextLink } from "@itandi/itandi-bb-ui/TextLink";
import { Th } from "@itandi/itandi-bb-ui/Th";
import { Thead } from "@itandi/itandi-bb-ui/Thead";
import { Tr } from "@itandi/itandi-bb-ui/Tr";
import Link from "next/link";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import useSWRMutation from "swr/mutation";
import { renderDropDownButton } from "~/components/common/parts/dropdown/DropDownButton";
import { SubtypeModal } from "~/components/specifics/settings/subtypes/SubtypeModal";
import { useSubtypes } from "~/hooks/useSubtypes";
import type { Subtype } from "~/models/Subtype";
import { PATH } from "~/pages/settings/subtypes/[subtypeId]/todo_templates";
import { PATH as REPAIR_PATH } from "~/pages/settings/subtypes/repair/todo_templates";
import { PATH as RESTORATION_PATH } from "~/pages/settings/subtypes/restoration/todo_templates";
import { isSuccess } from "~/utils/api/api_handler";
import { destroy } from "~/utils/api/Subtype";
import { generatePath } from "~/utils/generatePath";

const BASIC_DEAL_TYPES = [
	{ key: "restoration", name: "原状回復", path: RESTORATION_PATH },
	{ key: "repair", name: "修繕", path: REPAIR_PATH }
] as const satisfies { key: string; name: string; path: string }[];

export function SubtypeList() {
	const { data: subtypes } = useSubtypes().useIndex();
	const [editingSubtype, setEditingSubtype] = useState<Subtype | null>(null);
	const [deletingSubtype, setDeletingSubtype] = useState<Subtype | null>(null);

	const { trigger, isMutating } = useSWRMutation(
		generatePath("/subtypes"),
		async (_key, { arg }: { arg: { subtypeId: number } }) => {
			return await destroy(arg.subtypeId);
		}
	);

	const handleDeleteAccept = useCallback(async () => {
		const result = await trigger({ subtypeId: deletingSubtype!.id });
		if (isSuccess(result)) {
			toast.success("削除しました。");
			setDeletingSubtype(null);
		} else {
			toast.error(`削除に失敗しました。 ${result.message ?? ""}`);
		}
	}, [deletingSubtype, trigger]);

	if (subtypes === null) {
		return null;
	}

	return (
		<>
			<Table>
				<Thead>
					<Tr>
						<Th>
							<Label>タイプ名</Label>
						</Th>
						<Th width="10rem">
							<Label>取込キー</Label>
						</Th>
						<Th width="10rem">
							<Label>TODOテンプレート</Label>
						</Th>
						<Th width="6rem">
							<Label>操作</Label>
						</Th>
					</Tr>
				</Thead>
				<Tbody>
					{BASIC_DEAL_TYPES.map(({ key, name, path }) => (
						<Tr key={key}>
							<Td>{name}</Td>
							<Td>{key}</Td>
							<Td>
								<TextLink as={Link} color="link" href={path}>
									設定
								</TextLink>
							</Td>
							<Td>
								<Button
									disabled
									endIcon={<RiArrowDownSLine />}
									height="small"
									variant="secondary"
								>
									操作
								</Button>
							</Td>
						</Tr>
					))}
					{subtypes.map((subtype) => (
						<Tr key={subtype.id}>
							<Td>{subtype.name}</Td>
							<Td>{subtype.foreignKey}</Td>
							<Td>
								<TextLink as={Link} color="link" href={PATH(subtype.id)}>
									設定
								</TextLink>
							</Td>
							<Td>
								<Dropdown button={renderDropDownButton("操作")}>
									<DropdownMenu
										onClick={() => {
											setEditingSubtype(subtype);
										}}
									>
										編集
									</DropdownMenu>
									<DropdownMenu
										danger
										onClick={() => {
											setDeletingSubtype(subtype);
										}}
									>
										削除
									</DropdownMenu>
								</Dropdown>
							</Td>
						</Tr>
					))}
				</Tbody>
			</Table>
			{editingSubtype !== null ? (
				<SubtypeModal
					handleClose={() => {
						setEditingSubtype(null);
					}}
					subtype={editingSubtype}
				/>
			) : null}
			{deletingSubtype ? (
				<Modalv2
					isOpen
					onClose={() => {
						setDeletingSubtype(null);
					}}
					size="s"
				>
					<ModalHeader title="案件タイプの削除" />
					<ModalBody>
						案件タイプ「{deletingSubtype.name}」を削除します。
						<Alert mt={designTokens["spacing/m"]} variant="warning">
							この操作は元に戻せません。
						</Alert>
					</ModalBody>
					<ModalFooter
						leftArea={
							<Button
								disabled={isMutating}
								onClick={() => {
									setDeletingSubtype(null);
								}}
								variant="secondary"
							>
								キャンセル
							</Button>
						}
						rightArea={
							<Button
								loading={isMutating}
								onClick={handleDeleteAccept}
								variant="danger"
							>
								削除
							</Button>
						}
					/>
				</Modalv2>
			) : null}
		</>
	);
}
